import { Form, Header, Icon, Label } from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { ETATS_FACTURE } from '@/constants';
import { PayeurContext, RegieContext } from '@/contexts';
import type { PayeurFacture } from '@/types/payeur';
import { downloadFacturePdf, formatMonetaire } from '@/utils';

import { SquareTagContainer } from '../../Autre';
import EtatFactureSquareTags from '../../Facture/EtatFactureSquareTags';

const colors = {
  [ETATS_FACTURE.A_ENCAISSER]: null,
  [ETATS_FACTURE.ENCAISSEE]: 'green',
  [ETATS_FACTURE.ENCAISSEMENT_PARTIEL]: 'yellow',
  [ETATS_FACTURE.IMPAYE_PARTIEL]: 'orange',
  [ETATS_FACTURE.IMPAYE_TOTAL]: 'red',
};

const FactureAccordionTitle = ({ item }: { item: PayeurFacture }) => {
  const { identifiant, etat, cycle, lignes, identifiantDocument } = item;

  const { factures } = React.useContext(PayeurContext);
  const { regies, selectedRegie } = React.useContext(RegieContext);
  const regie = _.find(regies, { identifiant: selectedRegie });

  // Récupérer la facture complète depuis le contexte pour avoir accès aux propriétés des tags
  const factureComplete = React.useMemo(
    () => _.find(factures, { identifiant }),
    [factures, identifiant],
  );

  // Il faut récupérer la date la plus récente
  // dans factureComplete.lignes[].lignesRecu[].recu.dateCreation
  const dateDernierEncaissement = React.useMemo(() => {
    if (!factureComplete?.lignes) return null;

    // Récupérer toutes les dates de création des reçus
    const tousLesRecus = factureComplete.lignes
      .flatMap((ligne) => ligne.lignesRecu || [])
      .map((ligneRecu) => ligneRecu.recu)
      .filter(Boolean);

    // Trouver le reçu avec la date de création la plus récente
    const dernierRecu = _.maxBy(tousLesRecus, 'dateCreation');
    return dernierRecu?.dateCreation || null;
  }, [factureComplete]);

  const totalFacture = React.useMemo(() => _.sumBy(lignes, 'montant'), [lignes]);

  const renderSquareTagsRight = React.useMemo(() => {
    if (!factureComplete) return null;

    return (
      <EtatFactureSquareTags
        prelevementFilter="no-prelevement"
        etat={etat}
        regie={regie}
        dateFacture={cycle?.dateLimite || ''}
        dateFactureProlongee={
          regie?.regieProlongee
            ? dayjs(cycle?.dateLimite)
                .add(regie?.joursEncaissementSupplementaires, 'day')
                .format('YYYY-MM-DD')
            : null
        }
        prelevee={factureComplete?.prelevee || false}
        dateValidationPosteComptable={cycle?.dateValidationPosteComptable}
        dateRejet={factureComplete?.dateRejet}
        dateDernierEncaissement={dateDernierEncaissement}
        dateDerniereRelance={_.last(cycle?.relances)?.date}
      />
    );
  }, [factureComplete, dateDernierEncaissement, etat, regie, cycle]);

  const renderSquareTagsLeft = React.useMemo(() => {
    if (!factureComplete) return null;

    return (
      <EtatFactureSquareTags
        prelevementFilter="prelevement-only"
        etat={etat}
        regie={regie}
        dateFacture={cycle?.dateLimite || ''}
        dateFactureProlongee={
          regie?.regieProlongee
            ? dayjs(cycle?.dateLimite)
                .add(regie?.joursEncaissementSupplementaires, 'day')
                .format('YYYY-MM-DD')
            : null
        }
        prelevee={factureComplete?.prelevee || false}
        dateValidationPosteComptable={cycle?.dateValidationPosteComptable}
        dateRejet={factureComplete?.dateRejet}
        dateDernierEncaissement={dateDernierEncaissement}
        dateDerniereRelance={_.last(cycle?.relances)?.date}
      />
    );
  }, [factureComplete, dateDernierEncaissement, etat, regie, cycle]);

  return (
    <Form>
      <Form.Group fluid inline>
        <Form.Field width={3}>
          Facture n° <label>{item.numero}</label>
        </Form.Field>
        <Form.Field width={6}>
          <label>{`${cycle?.titre || ''} - ${dayjs(cycle?.dateEmission).format('DD/MM/YYYY')}`}</label>
        </Form.Field>
        <Form.Field width={3}>
          Montant : <label>{formatMonetaire(totalFacture)}</label>
        </Form.Field>
        <Form.Field width={3}>
          <Label
            color={colors[etat]}
            circular
            horizontal
            basic={etat === 'A encaisser'}
            style={{
              marginLeft: 0,
              marginRight: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5em',
              position: 'relative',
            }}
          >
            {React.Children.count(renderSquareTagsLeft) > 0 && (
              <SquareTagContainer position="left">{renderSquareTagsLeft}</SquareTagContainer>
            )}
            <Header as="h5" style={{ margin: 0, flex: 1 }} color={colors[etat]}>
              {etat}
            </Header>
            {React.Children.count(renderSquareTagsRight) > 0 && (
              <SquareTagContainer position="right">{renderSquareTagsRight}</SquareTagContainer>
            )}
          </Label>
        </Form.Field>
        <Form.Field width={1}>
          {!_.isNil(identifiantDocument) && (
            <Icon
              data-testid="button-pdf"
              name="file pdf"
              fitted
              link
              size="large"
              onClick={(e) => {
                // Le clic sur l'icône ne doit pas déplier/replier l'accordéon
                e.stopPropagation();
                downloadFacturePdf(identifiantDocument);
              }}
            />
          )}
        </Form.Field>
      </Form.Group>
    </Form>
  );
};

export default FactureAccordionTitle;
